import { Component, computed, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SwalComponent } from '@sweetalert2/ngx-sweetalert2';
import { forkJoin, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { tryGetErrorMessage } from '../../../error.utils';
import { LossPreventionService } from '../../../service/loss-prevention.service';
import { NotifyService } from '../../../service/notify.service';
import { Branch, LocationTiny, SubmitReviewRequest } from '../../../types';
import { PageSelectLocationComponent } from '../../components/page-select-location/page-select-location.component';
import { ReviewFormIncidentPhotosComponent } from '../loss-prevention/review-submit-form/review-form-incident-photos/review-form-incident-photos.component';
import { ReviewFormTransactionReportsComponent } from '../loss-prevention/review-submit-form/review-form-transaction-reports/review-form-transaction-reports.component';
import { ReviewFormService } from '../loss-prevention/review-submit-form/review-form.service';

@Component({
  selector: 'app-incident-submission',
  templateUrl: './incident-submission.component.html',
  styleUrl: './incident-submission.component.scss',
  standalone: false,
})
export class IncidentSubmissionComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private lpService = inject(LossPreventionService);
  private notifyService = inject(NotifyService);
  public reviewFormService = inject(ReviewFormService);
  private fb = inject(FormBuilder);
  private successModal = viewChild<SwalComponent>('success');
  private locationSelect = viewChild<PageSelectLocationComponent>('locationSelect');
  private incidentPhotos = viewChild<ReviewFormIncidentPhotosComponent>('incidentPhotos');
  private transactionReports = viewChild<ReviewFormTransactionReportsComponent>('transactionReports');

  // using a form for the branch and location selectors so that we don't mix signals & forms validation too much
  public branchLocationForm = this.fb.group({
    branchId: this.fb.control<number | null>(null, { validators: Validators.required }),
    locationId: this.fb.control<number | null>(null, { validators: Validators.required }),
  });

  public branch = signal<Branch | undefined>(undefined);
  public location = signal<(LocationTiny & { timezone: string }) | undefined>(undefined);

  public branchId = computed(() => this.branch()?.id);
  public locationId = computed(() => this.location()?.id);

  public isSubmitting = signal<boolean>(false);

  public ngOnInit(): void {
    this.reviewFormService.form.patchValue({ isTheft: true });
  }

  public ngOnDestroy() {
    this.reviewFormService.resetForm();
  }

  public onBranchChange(branch: Branch[] | Branch | null) {
    this.location.set(undefined);
    if (!branch) {
      this.branch.set(undefined);
      this.branchLocationForm.controls.branchId.setValue(null);
      this.branchLocationForm.controls.locationId.setValue(null);
      return;
    }

    this.branch.set(branch as Branch);
    this.branchLocationForm.controls.branchId.setValue((branch as Branch).id);
    this.branchLocationForm.controls.locationId.setValue(null);
    this.locationSelect().reset();
    this.locationSelect().getLocations(this.branchId());
  }

  public onLocationChange(location: LocationTiny[] | LocationTiny | null) {
    if (!location) return;
    this.location.set(location as LocationTiny & { timezone: string });
    this.branchLocationForm.controls.locationId.setValue((location as LocationTiny).id);
    this.reviewFormService.loadCasesByLocation(this.locationId());
  }

  public isFormValid() {
    return this.reviewFormService.form.valid && this.reviewFormService.findingsForm.valid && this.branchLocationForm.valid;
  }

  public submitReview() {
    if (!this.isFormValid()) return;

    const incidentForm = this.reviewFormService.form.value;
    const findingsForm = this.reviewFormService.findingsForm.getRawValue();

    const hasNewPhotos = incidentForm.uniqueInstancePhoto !== null && incidentForm.uniqueInstancePhoto.length > 0;
    const hasNewReports = incidentForm.reports !== null && incidentForm.reports.length > 0;

    this.isSubmitting.set(true);

    forkJoin([
      hasNewPhotos ? this.lpService.uploadFiles(incidentForm.uniqueInstancePhoto as File[]) : of(null),
      hasNewReports ? this.lpService.uploadFiles(incidentForm.reports as File[]) : of(null),
    ]).subscribe({
      next: (data) => {
        const uploadedPhotosLinks = [...(data[0] ?? []), ...incidentForm.existingUniqueInstancePhoto];
        const uploadedReportsLinks = [...(data[1] ?? []), ...incidentForm.existingReports];
        const submitRequest: SubmitReviewRequest = {
          is_theft: incidentForm.isTheft,
          case_id: incidentForm.matchingCase === 'new_case' ? null : (incidentForm.matchingCase as string | null),
          report_links: uploadedReportsLinks,
          photos_links: uploadedPhotosLinks,
          extra_links: incidentForm.extraLinks.map((l) => ({ link: l })),
          theft_type: {
            is_cancellation: incidentForm.theftType?.is_cancellation ?? false,
            is_underringing: incidentForm.theftType?.is_underringing ?? false,
            is_walkout: incidentForm.theftType?.is_walkout ?? false,
            is_cashier: incidentForm.theftType?.is_cashier ?? false,
          },
          suspected_items: incidentForm.suspectedItems?.map((i) => ({ id: i.id!, total: i.total })) ?? [],
          comments: findingsForm.comments,
          additional_stolen_value: incidentForm.additionalStolenValue,
          instance_at: this.reviewFormService.getValidInstanceAt(incidentForm.instance_at, null),
          recent_walk_in_ids: incidentForm.selectedImages,
        };

        this.lpService
          .submitSelfServiceReview(this.branchId(), this.locationId(), submitRequest)
          .pipe(finalize(() => this.isSubmitting.set(false)))
          .subscribe({
            next: () => this.successModal()?.fire(),
            error: (err) => this.notifyService.notify(`Failed to submit: ${tryGetErrorMessage(err)}`),
          });
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.notifyService.notify(`Failed to upload images: ${tryGetErrorMessage(err)}`);
      },
    });
  }

  public goBackHome() {
    this.router.navigate(['/home']);
  }

  public reset() {
    this.branch.set(undefined);
    this.location.set(undefined);
    this.branchLocationForm.reset();
    this.reviewFormService.resetForm();
    this.reviewFormService.form.patchValue({ isTheft: true });
    this.incidentPhotos()?.reset();
    this.transactionReports()?.reset();
  }
}
